import Konva from 'konva';
import { areaEffectGeometry, directionAtPointer, isDirectionalEffect } from './area_effect_geometry.js';

// Screen pixels: the rotate button stays usable while the source marker follows the grid.
export const ROTATION_HANDLE_SIZE = 36;
const ROTATION_HANDLE_GAP = 6;
export const ROTATE_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M19 9a8 8 0 1 0 0 7M19 3v6h-6" fill="none" stroke="white" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/><path d="M19 9a8 8 0 1 0 0 7M19 3v6h-6" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
)}") 12 12, crosshair`;

export const EffectRotationMethods = {
  _rotationHandleSize() {
    return this._usingTouch || this._mobileEditor ? 48 : ROTATION_HANDLE_SIZE;
  },
  _selectedDirectionalEffect() {
    if (this.readonlyValue || this._tool !== 'select' || !this._selectedShapeId) return null;
    const shape = this._findShapeById(this._selectedShapeId);
    return shape?.type === 'area-effect' && isDirectionalEffect(shape) ? shape : null;
  },

  _syncEffectRotationHandle() {
    const shape = this._selectedDirectionalEffect();
    if (!shape || !this._cursorLayer || !this._stage) return this._clearEffectRotationHandle();
    if (!this._effectRotationHandle) {
      this._effectRotationHandle = new Konva.Group({ name: 'effect-rotation-handle', listening: false });
      this._effectRotationIcon = new Konva.Group({ name: 'effect-rotate-control' });
      this._effectRotationIcon.add(
        new Konva.Rect({
          x: -ROTATION_HANDLE_SIZE / 2,
          y: -ROTATION_HANDLE_SIZE / 2,
          width: ROTATION_HANDLE_SIZE,
          height: ROTATION_HANDLE_SIZE,
          cornerRadius: 5,
          stroke: 'white',
          strokeWidth: 1,
          fill: 'rgba(0,0,0,0.7)'
        }),
        new Konva.Path({
          x: -12,
          y: -12,
          data: 'M19 9a8 8 0 1 0 0 7M19 3v6h-6',
          stroke: 'white',
          strokeWidth: 2,
          lineCap: 'round',
          lineJoin: 'round'
        })
      );
      this._effectRotationHandle.add(this._effectRotationIcon);
      this._cursorLayer.add(this._effectRotationHandle);
    }
    this._effectRotationHandle.position({
      x: shape.konvaShape.x() - shape.konvaShape.offsetX(),
      y: shape.konvaShape.y() - shape.konvaShape.offsetY()
    });
    const zoom = this._stage.scaleX();
    const size = this._areaEffectGrid().size * zoom;
    const handleSize = this._rotationHandleSize();
    this._effectRotationHandle.scale({ x: 1 / zoom, y: 1 / zoom });
    this._effectRotationIcon.scale({ x: handleSize / ROTATION_HANDLE_SIZE, y: handleSize / ROTATION_HANDLE_SIZE });
    const sourceSize = this._usingTouch || this._mobileEditor ? Math.max(size, handleSize) : size;
    this._effectRotationIcon.y(-sourceSize / 2 - ROTATION_HANDLE_GAP - handleSize / 2);
    this._cursorLayer.moveToTop();
    this._cursorLayer.batchDraw();
  },

  _clearEffectRotationHandle() {
    this._effectRotationHandle?.destroy();
    this._effectRotationHandle = null;
    this._effectRotationIcon = null;
    this._effectRotating = false;
    this._cursorLayer?.batchDraw();
  },

  _overEffectRotationHandle(point) {
    const shape = this._selectedDirectionalEffect();
    if (!shape || !point || !this._stage) return false;
    const origin = {
      x: shape.konvaShape.x() - shape.konvaShape.offsetX(),
      y: shape.konvaShape.y() - shape.konvaShape.offsetY()
    };
    const zoom = this._stage.scaleX();
    const x = (point.x - origin.x) * zoom;
    const y = (point.y - origin.y) * zoom;
    const size = this._rotationHandleSize();
    const sourceSize =
      this._usingTouch || this._mobileEditor
        ? Math.max(this._areaEffectGrid().size * zoom, size)
        : this._areaEffectGrid().size * zoom;
    const handleY = -sourceSize / 2 - ROTATION_HANDLE_GAP - size / 2;
    return Math.abs(x) <= size / 2 && Math.abs(y - handleY) <= size / 2;
  },

  _startEffectRotation(point) {
    if (!this._overEffectRotationHandle(point)) return false;
    this._finishShapeAnimations?.();
    this._effectRotating = true;
    this._effectRotationStartDegrees = this._selectedDirectionalEffect().directionDegrees;
    if (this.hasCursorTarget) this.cursorTarget.style.cursor = ROTATE_CURSOR;
    return true;
  },

  _rotateEffectAtPointer() {
    const shape = this._selectedDirectionalEffect();
    if (!shape) return;
    const direction = directionAtPointer(shape.konvaShape.position(), this._getWorldPointerPosition());
    if (direction === null || direction === shape.directionDegrees) return;
    shape.directionDegrees = direction;
    shape.konvaShape.setAttrs(areaEffectGeometry(shape, this._areaEffectGrid(), this._findShapeById(shape.parentId)));
    this._recreateLabels(shape);
    this._shapeTransformer?.forceUpdate();
    this._syncEffectRotationHandle();
    this._refreshShapePopover?.();
    this._shapesLayerAbove.batchDraw();
  },

  _finishEffectRotation({ cancel = false } = {}) {
    if (!this._effectRotating) return;
    this._effectRotating = false;
    const shape = this._selectedDirectionalEffect();
    if (shape && shape.directionDegrees !== this._effectRotationStartDegrees) {
      if (cancel) {
        shape.directionDegrees = this._effectRotationStartDegrees;
        this._syncAreaEffectGeometry();
      } else {
        this._save();
      }
    }
    this._refreshShapePopover?.();
    if (this.hasCursorTarget) this.cursorTarget.style.cursor = 'default';
  },

  _updateEffectRotationCursor() {
    if (this.hasCursorTarget) {
      this.cursorTarget.style.cursor = this._overEffectRotationHandle(this._getWorldPointerPosition())
        ? ROTATE_CURSOR
        : 'default';
    }
  }
};
